import { useEffect } from 'react';
import { Layout } from 'antd';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/common/Navbar.tsx';
import Footer from './components/common/Footer.tsx';
import { HomePage } from './components/home/HomePage.tsx';
import TournamentsPage from './components/tournaments/TournamentsPage.tsx';
// TournamentBracket используется в TournamentBracketContainer, но не напрямую в App
import { TournamentSchedule } from './components/tournaments/TournamentSchedule.tsx';
import { TournamentRegistration } from './components/tournaments/TournamentRegistration.tsx';
import Login from './components/auth/Login.tsx';
import { Register } from './components/auth/Register.tsx';
import ForgotPassword from './components/auth/ForgotPassword.tsx';
import ResetPassword from './components/auth/ResetPassword.tsx';
import VerifyEmail from './components/auth/VerifyEmail.tsx';
import AcceptManagerInvitation from './components/auth/AcceptManagerInvitation';
import { CreateTournament } from './components/tournaments/CreateTournament.tsx';
import EditTournament from './components/tournaments/EditTournament.tsx';
import AdminLayout from './components/admin/AdminLayout.tsx';
import DashboardPage from './components/admin/pages/DashboardPage.tsx';
import FederationsPage from './components/admin/pages/FederationsPage.tsx';
import ClubsPage from './components/admin/pages/ClubsPage.tsx';
import ManagersPage from './components/admin/pages/ManagersPage.tsx';
import CoachesPage from './components/admin/pages/CoachesPage.tsx';
import ParticipantsPage from './components/admin/pages/ParticipantsPage.tsx';
import CategoriesPage from './components/admin/pages/CategoriesPage.tsx';
import AdminTournamentsPage from './components/admin/pages/TournamentsPage.tsx';
import AdminSubscriptionsPage from './components/admin/pages/SubscriptionsPage.tsx';
import SubscriptionPlansPage from './components/admin/pages/SubscriptionPlansPage.tsx';
import PromoCodesPage from './components/admin/pages/PromoCodesPage.tsx';
import EmailSettingsPage from './components/admin/pages/EmailSettingsPage.tsx';
import SystemParametersPage from './components/admin/pages/SystemParametersPage.tsx';
import EmailTemplatesPage from './components/admin/pages/EmailTemplatesPage.tsx';
import InfoBlocksPage from './components/admin/pages/InfoBlocksPage.tsx';
import ClubManagement from './components/club/ClubManagement.tsx';
import ProfilePage from './components/profile/ProfilePage.tsx';
import ProfileSubscriptionsPage from './components/profile/SubscriptionsPage.tsx';
import SubscribePage from './components/profile/SubscribePage.tsx';
import SubscriptionReturnPage from './components/profile/SubscriptionReturnPage.tsx';
import ExtendedLicensePage from './components/profile/ExtendedLicensePage.tsx';
import ExtendedLicenseReturnPage from './components/profile/ExtendedLicenseReturnPage.tsx';
import ProtectedRoute from './components/common/ProtectedRoute.tsx';
import TournamentBracketContainer from './components/tournaments/TournamentBracketContainer';
import LiveScoreboard from './components/tournaments/LiveScoreboard';
import TournamentPage from './components/tournaments/TournamentPage';
import TournamentManagementPage from './components/tournaments/TournamentManagementPage';
import ContactForm from './components/feedback/ContactForm';
import PrivacyPolicyPage from './components/common/PrivacyPolicyPage';
import { MatchBroadcast } from './components/tournaments/MatchBroadcast';
import { MatchBroadcastControl } from './components/tournaments/MatchBroadcastControl';
import DocsPage from './components/docs/DocsPage';
import AdminDocsPage from './components/admin/pages/DocsPage';

const YANDEX_METRIKA_ID = 106841721;

const App = () => {
  const location = useLocation();
  const isBroadcastPage = location.pathname.includes('/broadcast') && !location.pathname.includes('/control');

  // Отправка просмотра в Яндекс.Метрику при смене маршрута (SPA)
  useEffect(() => {
    const w = window as unknown as { ym?: (id: number, action: string, url: string) => void };
    if (typeof w.ym === 'function') {
      w.ym(YANDEX_METRIKA_ID, 'hit', window.location.href);
    }
  }, [location.pathname]);

  return (
    <Layout>
      {!isBroadcastPage && <Navbar />}
      <Layout.Content style={{
        padding: '0',
        minHeight: isBroadcastPage ? '100vh' : 'calc(100vh - 64px - 70px)',
        marginTop: isBroadcastPage ? 0 : 64,
        background: isBroadcastPage ? 'transparent' : '#f0f2f5'
      }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/accept-manager-invitation" element={<AcceptManagerInvitation />} />
          <Route path="/tournaments" element={<TournamentsPage />} />
          <Route path="/tournaments/create" element={<CreateTournament />} />
          <Route path="/tournaments/:id" element={<TournamentPage />} />
          <Route path="/tournaments/:id/edit" element={<EditTournament />} />
          <Route path="/tournaments/:id/management" element={<ProtectedRoute><TournamentManagementPage /></ProtectedRoute>} />
          <Route path="/tournaments/:id/schedule" element={<TournamentSchedule />} />
          <Route path="/tournaments/register/:id" element={<TournamentRegistration />} />
          <Route path="/tournaments/:id/bracket" element={<TournamentBracketContainer />} />
          <Route path="/tournaments/:id/scoreboard" element={<LiveScoreboard />} />
          <Route path="/tournaments/:id/broadcast" element={<MatchBroadcast />} />
          <Route path="/tournaments/:id/broadcast/control" element={<ProtectedRoute><MatchBroadcastControl /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="federations" element={<FederationsPage />} />
            <Route path="clubs" element={<ClubsPage />} />
            <Route path="managers" element={<ManagersPage />} />
            <Route path="coaches" element={<CoachesPage />} />
            <Route path="participants" element={<ParticipantsPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="tournaments" element={<AdminTournamentsPage />} />
            <Route path="subscription-plans" element={<SubscriptionPlansPage />} />
            <Route path="promo-codes" element={<PromoCodesPage />} />
            <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="email-settings" element={<EmailSettingsPage />} />
            <Route path="email-templates" element={<EmailTemplatesPage />} />
            <Route path="system-parameters" element={<SystemParametersPage />} />
            <Route path="info-blocks" element={<InfoBlocksPage />} />
            <Route path="docs" element={<AdminDocsPage />} />
          </Route>
          <Route path="/club" element={<ProtectedRoute><ClubManagement /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/subscriptions" element={<ProtectedRoute><ProfileSubscriptionsPage /></ProtectedRoute>} />
          <Route path="/subscriptions/return" element={<SubscriptionReturnPage />} />
          <Route path="/extended-license" element={<ProtectedRoute><ExtendedLicensePage /></ProtectedRoute>} />
          <Route path="/extended-license/return" element={<ExtendedLicenseReturnPage />} />
          <Route path="/subscribe" element={<SubscribePage />} />
          <Route path="/contact" element={<ContactForm />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:sectionSlug/:pageSlug" element={<DocsPage />} />
        </Routes>
      </Layout.Content>
      {!isBroadcastPage && <Footer />}
    </Layout>
  );
};

export default App;
